import {
  lineItemResolutionSchema,
  type LineItemResolutionFormValues,
} from "@babackapp/return-widget-components/forms/line-item-resolution.form";
import { useAvailableResolutions } from "@babackapp/return-widget-components/hooks/use-available-resolutions";
import { useDraftReturn } from "@babackapp/return-widget-components/hooks/use-draft-return";
import { useUpdateLineItem } from "@babackapp/return-widget-components/hooks/use-update-line-item";
import type { OrderLineItem } from "@babackapp/return-widget-components/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useCurrentOrderLineItems } from "./order";
import { useWidgetParams } from "./use-widget-params";

type TopLevelResolution = "refund" | "exchange";

function toTopLevel(resolutions: Resolution[]): TopLevelResolution[] {
  const result: TopLevelResolution[] = [];
  if (resolutions.some((r) => r === "refund" || r === "discount"))
    result.push("refund");
  if (resolutions.some((r) => r === "exchange" || r === "exchange-credit"))
    result.push("exchange");
  return result;
}

function draftToTopLevel(
  resolution: string | null | undefined,
): TopLevelResolution | null {
  if (resolution === "exchange" || resolution === "exchange-credit")
    return "exchange";
  if (resolution === "refund" || resolution === "discount") return "refund";
  return null;
}

export function useLineItemResolutionForm() {
  const navigate = useNavigate();
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const { orderId, shopId } = useWidgetParams();
  const updateLineItem = useUpdateLineItem(orderId);
  const draft = useDraftReturn(orderId);
  const draftLineItem = draft?.lineItems.find(
    (li) => li.lineItemId === lineItemId,
  );
  const { data: lineItems = [] } = useCurrentOrderLineItems();

  const lineItem = lineItems.find((li) => li.line_item_id === lineItemId);

  const rawResolutions = useAvailableResolutions(
    shopId,
    lineItem ?? ({} as OrderLineItem),
  );

  const topLevelResolutions = toTopLevel(rawResolutions);
  const savedTopLevel = draftToTopLevel(draftLineItem?.resolution);
  const defaultResolution = savedTopLevel ?? topLevelResolutions[0] ?? "refund";

  const form = useForm<LineItemResolutionFormValues>({
    resolver: zodResolver(lineItemResolutionSchema),
    defaultValues: {
      resolution: defaultResolution,
      resolutionOption: null,
      exchangeVariantId: null,
    },
  });

  useEffect(() => {
    if (topLevelResolutions.length === 1) {
      updateLineItem(lineItemId, { resolution: topLevelResolutions[0] });
      navigate("../resolution-options", { replace: true });
    }
  }, [topLevelResolutions.length, lineItemId, navigate, updateLineItem]);

  const onSubmit = form.handleSubmit((values) => {
    const currentTopLevel = draftToTopLevel(draftLineItem?.resolution);
    if (values.resolution !== currentTopLevel) {
      updateLineItem(lineItemId, { resolution: values.resolution });
    }
    navigate("../resolution-options");
  });

  return {
    form,
    onSubmit,
    lineItemId,
    availableResolutions: topLevelResolutions,
  };
}
